// Caustic-light filter (WebGL + WebGPU). Apply it to a full-screen rect with blendMode "add":
// the output is pure light, masked by the rect's alpha, fading with depth.
import { Filter, GlProgram, GpuProgram } from "pixi.js";

const glVertex = /* glsl */ `in vec2 aPosition;
out vec2 vTextureCoord;
out vec2 vPixel;

uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;

vec4 filterVertexPosition(void) {
  vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
  position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
  position.y = position.y * (2.0 * uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
  return vec4(position, 0.0, 1.0);
}

vec2 filterTextureCoord(void) {
  return aPosition * (uOutputFrame.zw * uInputSize.zw);
}

void main(void) {
  gl_Position = filterVertexPosition();
  vTextureCoord = filterTextureCoord();
  vPixel = vTextureCoord * uInputSize.xy + uOutputFrame.xy;
}
`;

const glFragment = /* glsl */ `precision highp float;
in vec2 vTextureCoord;
in vec2 vPixel;
out vec4 finalColor;

// Note: no uniforms shared with the vertex stage (precision mismatches fail to link on WebKit).
uniform sampler2D uTexture;

uniform float uTime;
uniform float uIntensity;
uniform vec2 uSize;
uniform vec3 uColor;

float caustic(vec2 uv, float t) {
  vec2 p = mod(uv * 6.28318, 6.28318) - 250.0;
  vec2 i = p;
  float c = 1.0;
  float inten = 0.005;
  for (int n = 0; n < 4; n++) {
    float tt = t * (1.0 - (3.5 / float(n + 1)));
    i = p + vec2(cos(tt - i.x) + sin(tt + i.y), sin(tt - i.y) + cos(tt + i.x));
    c += 1.0 / length(vec2(p.x / (sin(i.x + tt) / inten), p.y / (cos(i.y + tt) / inten)));
  }
  c /= 4.0;
  c = 1.17 - pow(c, 1.4);
  return clamp(pow(abs(c), 8.0), 0.0, 1.5);
}

void main(void) {
  vec4 src = texture(uTexture, vTextureCoord);
  vec2 px = vPixel;
  vec2 uv = px / max(uSize.y, 1.0);
  float c = caustic(uv * 0.9 + vec2(0.13, 0.27), uTime * 0.5);
  float depth = clamp(1.0 - px.y / max(uSize.y, 1.0), 0.0, 1.0);
  float a = c * uIntensity * (0.25 + 0.75 * depth) * src.a;
  finalColor = vec4(uColor * a, a);
}
`;

const wgsl = /* wgsl */ `
struct GlobalFilterUniforms {
  uInputSize: vec4<f32>,
  uInputPixel: vec4<f32>,
  uInputClamp: vec4<f32>,
  uOutputFrame: vec4<f32>,
  uGlobalFrame: vec4<f32>,
  uOutputTexture: vec4<f32>,
};

struct CausticUniforms {
  uTime: f32,
  uIntensity: f32,
  uSize: vec2<f32>,
  uColor: vec3<f32>,
};

@group(0) @binding(0) var<uniform> gfu: GlobalFilterUniforms;
@group(0) @binding(1) var uTexture: texture_2d<f32>;
@group(0) @binding(2) var uSampler: sampler;
@group(1) @binding(0) var<uniform> causticUniforms: CausticUniforms;

struct VSOutput {
  @builtin(position) position: vec4<f32>,
  @location(0) uv: vec2<f32>,
};

fn filterVertexPosition(aPosition: vec2<f32>) -> vec4<f32> {
  var position = aPosition * gfu.uOutputFrame.zw + gfu.uOutputFrame.xy;
  position.x = position.x * (2.0 / gfu.uOutputTexture.x) - 1.0;
  position.y = position.y * (2.0 * gfu.uOutputTexture.z / gfu.uOutputTexture.y) - gfu.uOutputTexture.z;
  return vec4(position, 0.0, 1.0);
}

fn filterTextureCoord(aPosition: vec2<f32>) -> vec2<f32> {
  return aPosition * (gfu.uOutputFrame.zw * gfu.uInputSize.zw);
}

@vertex
fn mainVertex(@location(0) aPosition: vec2<f32>) -> VSOutput {
  return VSOutput(filterVertexPosition(aPosition), filterTextureCoord(aPosition));
}

fn caustic(uv: vec2<f32>, t: f32) -> f32 {
  let tau = 6.28318;
  let q = uv * tau;
  let p = q - floor(q / tau) * tau - vec2(250.0, 250.0);
  var i = p;
  var c = 1.0;
  let inten = 0.005;
  for (var n: i32 = 0; n < 4; n = n + 1) {
    let tt = t * (1.0 - (3.5 / f32(n + 1)));
    i = p + vec2(cos(tt - i.x) + sin(tt + i.y), sin(tt - i.y) + cos(tt + i.x));
    c = c + 1.0 / length(vec2(p.x / (sin(i.x + tt) / inten), p.y / (cos(i.y + tt) / inten)));
  }
  c = c / 4.0;
  c = 1.17 - pow(abs(c), 1.4);
  return clamp(pow(abs(c), 8.0), 0.0, 1.5);
}

@fragment
fn mainFragment(@location(0) uv: vec2<f32>, @builtin(position) position: vec4<f32>) -> @location(0) vec4<f32> {
  let src = textureSample(uTexture, uSampler, uv);
  let px = uv * gfu.uInputSize.xy + gfu.uOutputFrame.xy;
  let sy = max(causticUniforms.uSize.y, 1.0);
  let c = caustic(px / sy * 0.9 + vec2(0.13, 0.27), causticUniforms.uTime * 0.5);
  let depth = clamp(1.0 - px.y / sy, 0.0, 1.0);
  let a = c * causticUniforms.uIntensity * (0.25 + 0.75 * depth) * src.a;
  return vec4(causticUniforms.uColor * a, a);
}
`;

export interface CausticUniforms {
  uTime: number;
  uIntensity: number;
  uSize: Float32Array;
  uColor: Float32Array;
}

export class CausticFilter extends Filter {
  constructor() {
    super({
      glProgram: GlProgram.from({ vertex: glVertex, fragment: glFragment, name: "groovezoo-caustics" }),
      gpuProgram: GpuProgram.from({
        vertex: { source: wgsl, entryPoint: "mainVertex" },
        fragment: { source: wgsl, entryPoint: "mainFragment" },
      }),
      resources: {
        causticUniforms: {
          uTime: { value: 0, type: "f32" },
          uIntensity: { value: 0.5, type: "f32" },
          uSize: { value: new Float32Array([1, 1]), type: "vec2<f32>" },
          uColor: { value: new Float32Array([0.75, 0.95, 1.0]), type: "vec3<f32>" },
        },
      },
      // Caustics are soft; render at half resolution to save fill rate.
      resolution: 0.5,
    });
  }

  private get u(): CausticUniforms {
    return this.resources.causticUniforms.uniforms as CausticUniforms;
  }

  set time(v: number) {
    this.u.uTime = v;
  }
  set intensity(v: number) {
    this.u.uIntensity = v;
  }
  setSize(w: number, h: number): void {
    this.u.uSize[0] = w;
    this.u.uSize[1] = h;
  }
  setColor(c: number): void {
    this.u.uColor[0] = ((c >> 16) & 255) / 255;
    this.u.uColor[1] = ((c >> 8) & 255) / 255;
    this.u.uColor[2] = (c & 255) / 255;
  }
}
